import {useEffect, useState} from "react";
import {SponsorApi, type SponsorAvailabilityDto} from "sponsor/SponsorApi";

/**
 * The one availability read the `/advertising` landing page makes (ui#1861).
 *
 * A thin adapter and nothing more: it holds a piece of state, calls the api once and hands the result
 * back. Every rule about what the numbers MEAN lives outside React — the run-length cell reads
 * `maxDays` directly, and the fourteen-day row is computed by `advertisingAvailability.ts`, which is
 * plain TypeScript with its own tests.
 *
 * ## Why it is a hook rather than a loader
 *
 * The page is PRE-RENDERED. Effects do not run during the SSG pass, so nothing here executes in Node
 * and the static HTML ships without any of it — which is the correct outcome twice over: a marketing
 * page must not bake today's operator policy into a file a CDN then serves for a week, and the static
 * HTML must never describe content it does not contain. The cells this read feeds are absent from the
 * pre-render and arrive after hydration, exactly as the hero's art does.
 *
 * ## Why a failure is silent
 *
 * `GET /sponsor/availability` is anonymous, so there is no signed-out case to handle — but it can still
 * fail, and when it does this page has nothing true to say about run length or the next free day. It
 * says nothing: `null` renders the bands WITHOUT those parts rather than with a spinner that never
 * resolves or a zero that reads as "no days left". An error banner would be worse still; a visitor who
 * came to read about advertising cannot act on our api being down, and the rest of the page is intact.
 *
 * @returns the published availability, or `null` before it arrives and after a failed read
 */
export function useAdvertisingAvailability(): SponsorAvailabilityDto | null {
	const [availability, setAvailability] = useState<SponsorAvailabilityDto | null>(null);
	useEffect(() => {
		// Guarded against a resolve after unmount: this is a public page a reader leaves quickly, and a
		// setState on a dead component is a warning that trains people to ignore warnings.
		let mounted = true;
		SponsorApi.getAvailability()
			.then(loaded => {
				if (mounted) {
					setAvailability(loaded);
				}
			})
			.catch(() => {
				// Deliberately nothing. `null` is already the state that renders the page without the
				// live figures, and it is the state we are already in.
			});
		return () => {
			mounted = false;
		};
	}, []);
	return availability;
}
